import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AuditSubjectGroup } from '@lms/shared-types'
import { listAuditPage } from '../../../api/audit'
import { ThinkingDots } from '../../primitives/ThinkingDots'
import { CategoryChip } from './ChangeDiff'
import { AuditTimelineEvent } from './AuditTimelineEvent'
import { formatTimestamp, humanizeSubjectType, relativeTime } from './audit-format'

/** How many events to pull when a group is expanded. Objects with more than
 *  this are rare; we show the most recent and note the rest. */
const EVENT_FETCH_LIMIT = 100

/** Number of columns in the grouped table — the expanded row spans them all.
 *  Columns: caret, Category, Entity, Latest event, Last changed by, When. */
export const AUDIT_GROUP_COLSPAN = 6

function subjectTitle(group: AuditSubjectGroup): string {
  if (group.subjectLabel) return group.subjectLabel
  const shortId = group.subjectId ? ` · ${group.subjectId.slice(0, 8)}` : ''
  return `${humanizeSubjectType(group.subjectType)}${shortId}`
}

const cell: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 13,
  verticalAlign: 'middle',
  borderTop: '1px solid var(--french-gray-2)',
}

/** One object as a table row: a clickable summary spanning the column set,
 *  plus a full-width row beneath it holding the expandable change timeline. */
export function AuditGroupRow({ group }: { group: AuditSubjectGroup }): JSX.Element {
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(false)

  const events = useQuery({
    queryKey: ['audit-subject-events', group.subjectType, group.subjectId],
    queryFn: () =>
      listAuditPage({
        subjectType: group.subjectType,
        ...(group.subjectId ? { subjectId: group.subjectId } : {}),
        limit: EVENT_FETCH_LIMIT,
      }),
    enabled: open,
    staleTime: 30_000,
  })

  const toggle = (): void => setOpen((v) => !v)

  return (
    <tbody data-testid="audit-group" data-subject-type={group.subjectType}>
      <tr
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-testid="audit-group-toggle"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          cursor: 'pointer',
          background: open || hover ? 'var(--surface)' : 'transparent',
          transition: 'background 80ms ease-out',
        }}
      >
        <td style={{ ...cell, width: 30, color: 'var(--text-3)', textAlign: 'center' }}>
          {open ? '▾' : '▸'}
        </td>
        <td style={{ ...cell, width: 118 }}>
          {group.category ? (
            <CategoryChip category={group.category} label={group.subjectKind} />
          ) : null}
        </td>
        <td
          style={{
            ...cell,
            maxWidth: 0,
            fontWeight: 600,
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          data-testid="audit-group-title"
          title={subjectTitle(group)}
        >
          {subjectTitle(group)}
        </td>
        <td style={{ ...cell, width: 200, color: 'var(--text-2)' }}>
          {group.latestLabel || group.latestAction}
        </td>
        <td style={{ ...cell, width: 150, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
          {group.latestActorName ?? 'System'}
          {group.latestActorRole ? (
            <span style={{ color: 'var(--text-3)', fontSize: 11 }}> ({group.latestActorRole})</span>
          ) : null}
        </td>
        <td
          style={{ ...cell, width: 150, color: 'var(--text-3)', whiteSpace: 'nowrap' }}
          title={formatTimestamp(group.lastChangedAt)}
        >
          {relativeTime(group.lastChangedAt)}
        </td>
      </tr>

      {open ? (
        <tr>
          <td colSpan={AUDIT_GROUP_COLSPAN} style={{ padding: 0, borderTop: 'none' }}>
            <div
              data-testid="audit-group-events"
              style={{ padding: '8px 14px 16px 40px', background: 'var(--surface)' }}
            >
              {events.isLoading ? (
                <div style={{ padding: 8 }}>
                  <ThinkingDots label="Loading history" />
                </div>
              ) : events.isError ? (
                <div style={{ padding: 8, fontSize: 12, color: 'var(--danger-fg)' }}>
                  Could not load this object&apos;s history.
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* the vertical connector line the node dots sit on */}
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      left: 6,
                      top: 4,
                      bottom: 10,
                      width: 1,
                      background: 'var(--french-gray-2)',
                    }}
                  />
                  <ul role="list" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {(events.data?.data ?? []).map((entry) => (
                      <AuditTimelineEvent key={entry.id} entry={entry} />
                    ))}
                  </ul>
                  {(events.data?.meta.total ?? 0) > (events.data?.data.length ?? 0) ? (
                    <div style={{ paddingLeft: 26, fontSize: 11, color: 'var(--text-3)' }}>
                      Showing the {events.data?.data.length} most recent of{' '}
                      {events.data?.meta.total} events.
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </tbody>
  )
}
